import { Suspense, StrictMode, ReactChild, useMemo } from 'react';
import { BrowserRouter, Route } from 'react-router-dom';
import { Provider } from 'react-redux';

import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { ThemeProvider as MuiThemeProvider, Theme, StyledEngineProvider } from '@mui/material/styles';
import { enGB, nb, sv } from 'date-fns/locale';

import { theme } from './styles/muiTheme';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components/macro';

import store from './store';
import { Allow, RedirectToAccount, WithAccountAccessToken } from './auth';
import AppBar from './AppBar';
import AppRoutes from './AppRoutes';
import { WithErrorHandler } from './errors';
import './sentry';
import './i18n';
import { Redirect } from 'react-router';
import Root from './components/Root';
import { Welcome } from './welcome';
import { PUBLIC_URL } from './env';
import { ActionsProvider } from './Actions';
import { AccountIdChangedDispatcher } from './auth/useAccountId';
import { useTranslation } from 'react-i18next';
import { Messages } from './messages';
import RedirectToPullRequestRoute from './RedirectToPullRequestRoute';

// Setting basename in BrowserRouter so that pull requests can be served from a relative path
const basename = PUBLIC_URL ? PUBLIC_URL : undefined;

function App() {
    return (
        <StrictMode>
            <StyledEngineProvider injectFirst>
                <MuiThemeProvider theme={theme}>
                    <StyledComponentsThemeProvider theme={theme}>
                        <Root>
                            <Suspense fallback={null}>
                                <Provider store={store}>
                                    <Localization>
                                        <BrowserRouter basename={basename}>
                                            <WithErrorHandler>
                                                <Route path={'/_pr'} component={RedirectToPullRequestRoute} />
                                                <Route path={'/'} exact component={RedirectToAccount} />
                                                <Route path={'/login'} render={() => <Redirect to="/" />} />
                                                <Route
                                                    path={`/:accountId`}
                                                    render={(props) => (
                                                        <ActionsProvider>
                                                            <Messages />
                                                            <AccountIdChangedDispatcher />
                                                            <WithAccountAccessToken {...props}>
                                                                <AppBar />
                                                                <Suspense fallback={null}>
                                                                    <AppRoutes />
                                                                </Suspense>
                                                                <Allow anyOfScopes={[['admin:accounts']]}>
                                                                    <Welcome />
                                                                </Allow>
                                                            </WithAccountAccessToken>
                                                        </ActionsProvider>
                                                    )}
                                                />
                                            </WithErrorHandler>
                                        </BrowserRouter>
                                    </Localization>
                                </Provider>
                            </Suspense>
                        </Root>
                    </StyledComponentsThemeProvider>
                </MuiThemeProvider>
            </StyledEngineProvider>
        </StrictMode>
    );
}

const langLocaleMap = {
    'en': enGB,
    'no': nb,
    'se': sv,
};

const Localization = ({ children }: { children: ReactChild }) => {
    const { i18n: { language } } = useTranslation('app');
    const locale = useMemo(() => {
        return langLocaleMap[language as keyof typeof langLocaleMap];
    }, [language]);

    return (
        <LocalizationProvider
            dateAdapter={AdapterDateFns}
            locale={locale}
        >
            {children}
        </LocalizationProvider>
    );
};

declare module 'styled-components' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {}
}

declare module '@mui/material/styles' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {}
}

export default App;
